"use client";

import { useTranslations } from "@packages/i18n";
import { useRequestJournal, useRequestJournals } from "@packages/sdk";
import { Toast, useModal } from "@packages/ui";
import type { ReactNode } from "react";
import { useState } from "react";

import type { JournalEditorProps } from "./JournalEditor";
import { JournalEditor } from "./JournalEditor";

export const useJournalEditor = ({
  journal,
  prayer,
}: Omit<JournalEditorProps, "onSubmit"> = {}): {
  show: (props?: Omit<JournalEditorProps, "onSubmit">) => Promise<void>;
  showToast: boolean;
  toast: ReactNode;
} => {
  const modal = useModal(JournalEditor);
  const { mutationPost: addJournal, infiniteQuery: journals } =
    useRequestJournals();
  const { mutationPut: editJournal, query: oneJournal } = useRequestJournal({
    id: journal?.id,
  });
  const [showToast, setShowToast] = useState<boolean>(false);
  const t = useTranslations();

  const show = async ({
    journal: journalOverride,
    prayer: prayerOverride,
  }: Omit<JournalEditorProps, "onSubmit"> = {}) => {
    const saved = await modal.show({
      journal: journalOverride ?? journal ?? null,
      prayer: prayerOverride ?? prayer ?? null,
      onSubmit: async (data) => {
        if (journal) {
          await editJournal.mutateAsync({ journal: data });
        } else {
          await addJournal.mutateAsync({
            journal: {
              ...data,
              title: prayerOverride?.prompt ?? prayer?.prompt ?? data.title,
            },
          });
        }
        modal.resolve(true);
        return modal.hide();
      },
    });
    if (saved) {
      setShowToast(true);
      if (journal) oneJournal.refetch();
      journals.refetch();
    }
  };

  return { show, showToast, toast: <Toast>{t("journal_saved")}</Toast> };
};
